import { RouterProvider, createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';

import Header from './components/Header';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import WhatWeDo from './components/WhatWeDo';
import FeaturedProperties from './components/FeaturedProperties';
import WhyChooseUs from './components/WhyChooseUs';
import ValuationForm from './components/ValuationForm';
import Footer from './components/Footer';

import Properties from './pages/Properties';
import PropertyDetails from './pages/PropertyDetails';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/auth/Login';

import AdminRoutes from './routes/adminRoutes';

import { useAuth } from './hooks/useAuth';
import { ErrorProvider, useError } from './context/ErrorContext';
import ErrorModal from './components/common/ErrorModal';
import ErrorBoundary from './components/common/ErrorBoundary';

import './App.css';
import { Toaster } from 'sonner';
import NotFound from './pages/NotFound';
import GodrejBarcaMSR from './pages/GodrejBarcaMSR';
import GodrejShettigere from './pages/GodrejShettigere';
import {Helmet, HelmetProvider } from 'react-helmet-async';
import EligibilityChecker from './pages/Loan/EligibilityCheck';
import LoanCalculators from './pages/Loan/LoanCalucaltor';
import HomeLoan from './pages/Loan/HomeLoan';
import HomeLoanCalculatorCTA from './pages/HomeLoanCaluclatorCTA';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';

gsap.registerPlugin(ScrollTrigger);

const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/aadashmin');

  useEffect(() => {
    
    window.scrollTo(0, 0);

    let lenis;

    if (!isAdminRoute) {
      lenis = new Lenis({
        duration: 0.7, 
        easing: (t) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t),
        orientation: 'vertical',
        smoothWheel: false,
        smoothTouch: true,
        touchMultiplier: 2,
        wheelMultiplier: 1,
        lerp: 0.1 
      });

      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });
    }

    return () => {
      if (lenis) {
        lenis.destroy();
        gsap.ticker.remove();
      }
    };
  }, [isAdminRoute, location.pathname]);

  return (
    <div className="relative w-full min-h-screen overflow-x-hidden">
      {!isAdminRoute && <Header />}
      <main className="w-full">
        {children}
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
};

const HomePage = () => (
  <>
    <Helmet>
      <title>Home | Antacres</title>
      <meta 
        name="description" 
        content="Antacres - Your trusted real estate partner for finding premium properties in India." 
      />
    </Helmet>

    <Hero />
    <div className="w-full bg-white">
      <FeaturedProperties />
      <HomeLoanCalculatorCTA />
      <WhatWeDo />
      <HowItWorks />
      <WhyChooseUs />
      <ValuationForm />
    </div>
  </>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/antacres-aadashminx" replace />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout><HomePage /></Layout>,
  },
  {
    path: "/properties",
    element: <Layout><Properties /></Layout>,
  },
  {
    path: "/properties/:slug",
    element: <Layout><PropertyDetails /></Layout>,
  },
  {
    path: "/about",
    element: <Layout><About /></Layout>,
  },
  {
    path: "/contact",
    element: <Layout><Contact /></Layout>,
  },
  {
    path: "/eligibility",
    element: <Layout><EligibilityChecker /></Layout>,
  },

{
    path: "/loancalculator",
    element: <Layout><LoanCalculators /></Layout>,
  },

  {
    path: "/homeloan",
    element: <Layout><HomeLoan /></Layout>,
  },
  {
    path: "/privacy",
    element: <Layout><PrivacyPolicy /></Layout>,
  },
  {
    path: "/terms",
    element: <Layout><TermsAndConditions /></Layout>,
  },



  {
    path: "/antacres-aadashminx",
    element: <Login />,
  },
  {
    path: "/aadashmin/*",
    element: (
      <ProtectedRoute>
        <Layout>
          <AdminRoutes />
        </Layout>
      </ProtectedRoute>
    ),
  },
  // Godrej Project Landing Pages
    {
      path: '/godrej-shettigere',
      element: <Layout><GodrejShettigere /></Layout>,
    },
    {
      path: '/godrej-barca',
      element: <Layout><GodrejBarcaMSR /></Layout>,
    },

  {
    path: "*",
    element: <NotFound />
  }
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
});

const AppContent = () => {
  return <RouterProvider router={router} />;
};

const ErrorHandler = ({ children }) => {
  const { showError } = useError();

  useEffect(() => {
    const handleError = (event) => {
      event.preventDefault();
      showError(event.error?.message || 'An unexpected error occurred', 'error');
    };

    const handleUnhandledRejection = (event) => {
      event.preventDefault();
      showError(event.reason?.message || 'An unexpected error occurred', 'error');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [showError]);

  return children;
};

function App() {
  return (
    <ErrorProvider>
      <ErrorBoundary>
        <ErrorHandler>
          <Toaster richColors position="top-right" />
          <HelmetProvider>
          <AppContent />
          </HelmetProvider>
          <ErrorModal />
        </ErrorHandler>
      </ErrorBoundary>
    </ErrorProvider>
  );
}

export default App;